import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { ClassAttributes, createElement } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  CreativeFormat,
  CreativeFormatAspectRatio,
  CreativeFormatPlaybackTime,
} from '../../../graphql/generated/graphql';
import { toOfferCreativeFormatCreativeTypeKey, toOfferCreativeFormatNetworkKey } from '../../../utility/graphql/enum';
import CreativeFormatCreativeTypeLabel from '../../component/compound/CreativeFormatCreativeTypeLabel';
import CreativeFormatNetworkIcon from '../../component/compound/CreativeFormatNetworkIcon';
import CreativeFormatPlacementLabel from '../../component/compound/CreativeFormatPlacementLabel';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';

interface Props {
  creativeFormat: {
    network: Pick<CreativeFormat['network'], 'key'>;
    placement: Pick<CreativeFormat['placement'], 'key' | 'name'>;
    creativeType: Pick<CreativeFormat['creativeType'], 'key'>;
    attribute: {
      playbackTime?: {
        seconds: CreativeFormatPlaybackTime['seconds'];
      } | null;
      aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
    };
    resizeBaseId?: CreativeFormat['resizeBaseId'];
  };
  className?: string;
}

const CreativeFormatDisplay = ({ creativeFormat, className }: ClassAttributes<HTMLElement> & Props) => {
  const { network, placement, creativeType, attribute, resizeBaseId } = creativeFormat;
  return (
    <Root className={className}>
      <FormatDisplay>
        <Network>
          <Type>
            <CreativeFormatNetworkIcon network={toOfferCreativeFormatNetworkKey(network.key)} />
          </Type>
        </Network>
        <Placement>
          <Type>
            <CreativeFormatPlacementLabel placement={placement} />
          </Type>
        </Placement>
        <CreativeType>
          <CreativeFormatCreativeTypeLabel creativeType={toOfferCreativeFormatCreativeTypeKey(creativeType.key)} />
        </CreativeType>
        <PlaybackTime>
          <Type>
            {attribute.playbackTime ? (
              <FormattedMessage
                id="creative_format.playback_time"
                values={{ seconds: attribute.playbackTime.seconds }}
              />
            ) : null}
          </Type>
        </PlaybackTime>
        <AspectRatio>
          <Type>
            {attribute.aspectRatio &&
            attribute.aspectRatio.aspectRatio.width * attribute.aspectRatio.aspectRatio.height > 0 ? (
              `${attribute.aspectRatio.aspectRatio.width}:${attribute.aspectRatio.aspectRatio.height} (${attribute.aspectRatio.size.width} x ${attribute.aspectRatio.size.height})`
            ) : (
              <FormattedMessage id="creative_format.aspect_ratio.nullable" />
            )}
          </Type>
        </AspectRatio>
        <Option>
          <TypeSmall>{resizeBaseId ? <FormattedMessage id="creative_format.resize" /> : null}</TypeSmall>
        </Option>
      </FormatDisplay>
    </Root>
  );
};

const Root = styled('div')(flexibleLayoutContainer({ direction: 'row', alignItems: 'center' }), {
  lineHeight: 0,
});

const FormatDisplay = styled('div')(
  gridLayoutContainer({
    columns: ['8%', '1fr', '14%', '10%', '34%', '20%'],
    rows: ['auto'],
    areas: [['network', 'placement', 'creative_type', 'playback_time', 'aspect_ratio', 'option']],
    justifyContent: 'space-between',
    alignItems: 'center',
  }),
  {
    width: '100%',
  },
);

const Network = styled('div')(gridLayoutItem('network'));
const Placement = styled('div')(gridLayoutItem('placement'));
const CreativeType = styled('div')(gridLayoutItem('creative_type', { justifySelf: 'center' }));
const AspectRatio = styled('div')(gridLayoutItem('aspect_ratio'));
const PlaybackTime = styled('div')(gridLayoutItem('playback_time', { justifySelf: 'center' }));
const Option = styled('div')(gridLayoutItem('option', { justifySelf: 'center' }));
const Type = styled(Typography)({
  fontSize: '0.8125rem',
  lineHeight: 1,
  wordBreak: 'break-word',
});
const TypeSmall = styled(Typography)({
  fontSize: '0.6875rem',
  lineHeight: 1,
});

export default CreativeFormatDisplay;
